<template>
    <DataTable
        ref="dt"
        class="p-datatable-sm text-sm"
        removableSort
        dataKey="id"
        filterDisplay="menu"
        responsiveLayout="scroll"
        v-model:filters="filters"
        v-model:selection="selectedDataSource"
        :value="dataSourceList"
        :lazy="true"
        :paginator="true"
        :rows="totalRowShowed"
        :totalRecords="total_records"
        :scrollable="true"
        :selectAll="selectAll"
        :rowsPerPageOptions="[10, 25, 50, 100,total_records]"
        :globalFilterFields="config.globalFilter"
        :sortField="lazyParams.sortField"
        :sortOrder="lazyParams.sortOrder"
        @page="onPaginate($event)"
        @sort="onSort($event)"
        @filter="onFilter($event)"
        @select-all-change="onSelectAllChange"
        @row-select="onRowSelect"
        @row-unselect="onRowUnselect"
    >
        <template #header>
            <div class="flex justify-between">
                <h2 class="p-3">{{ config.dtHeader }}</h2>
                <div>
                    <Button type="button" class="p-button-outlined mr-2" icon="pi pi-external-link" label="Export" @click="exportCSV($event)" />
                    <Button type="button" class="p-button-outlined mr-2" icon="pi pi-file-excel" label="Export Excel" @click="exportExcel()" />
                    <Button
                        type="button"
                        icon="pi pi-filter-slash"
                        label="Clear"
                        class="p-button-outlined mr-2"
                        @click="resetFilter"
                    />
                    <span class="p-input-icon-left">
                        <i class="pi pi-search" />
                        <Input
                            @keydown.enter="onFilter()"
                            v-model="filters['global'].value"
                            placeholder="Cari..."
                        />
                    </span>
                </div>
            </div>
        </template>
        <template #empty> Data tidak ditemukan </template>
        <Column
            selectionMode="multiple"
            style="width: 3rem;"
            frozen
        ></Column>
        <Column frozen>
            <template #body="slotProps" >
                <Link v-if="config.dtSourceApiRoute.edit" :href="route(config.dtSourceApiRoute.edit, { id: slotProps.data.id})" class="p-button p-component p-button-icon-only p-button-rounded p-button-outlined mr-1 hover:bg-[#f5f5f5] "><i class="pi pi-pencil"></i></Link>
                <Button v-if="config.dtSourceApiRoute.destroy" icon="pi pi-trash" aria-label="Delete" @click="deleteData($event, slotProps.data)" class="p-button-danger p-button-rounded p-button-outlined" />
                <ButtonLinkShow v-if="config.dtSourceApiRoute.show" :url="route(config.dtSourceApiRoute.show, { id: slotProps.data.id})">Lihat</ButtonLinkShow>
            </template>
        </Column>
        <Column
            v-for="col in config.defineCols"
            :key="col.field"
            :field="col.field"
            :header="col.header"
            :ref="col.field"
            :style="col.style"
            :frozen="col.isFrozen !== undefined ? col.isFrozen : false"
            :dataType="col.col_type"
            :sortable="col.isSortAble !== undefined ? col.isSortAble : true"
            :showFilterMenu="
                col.isFilterAble !== undefined ? col.isFilterAble : true
            "
        >
            <template #filter="{ filterModel, filterCallback }">
                <Input
                    type="text"
                    v-model="filterModel.value"
                    @keydown.enter="filterCallback()"
                    class="p-column-filter"
                    placeholder="Cari..."
                    v-if="col.col_type == 'text'"
                />
                <div v-else-if="col.col_type == 'boolean'">
                    <TriStateCheckbox v-model="filterModel.value" />
                    <span class="ml-3 font-medium text-sm text-gray-700">{{
                        filterModel.value == null
                            ? "All"
                            : filterModel.value == true
                            ? "Yes"
                            : "No"
                    }}</span>
                </div>
                <Calendar
                    v-model="filterModel.value"
                    dateFormat="mm/dd/yy"
                    placeholder="mm/dd/yyyy"
                    v-else-if="col.col_type == 'date'"
                />
                <Input
                    type="text"
                    v-model="filterModel.value"
                    @keydown.enter="filterCallback()"
                    class="p-column-filter"
                    placeholder="Cari..."
                    v-if="col.col_type == 'numeric'"
                />
            </template>

            <template #body="{ data }">
                <span v-if="col.col_type == 'text' && col.col_display == 'image'">
                    <div class="flex justify-center">
                        <div v-if="getRealColValue(data, col.field)" class="w-16 h-16 overflow-hidden border border-gray-200 shadow-sm rounded-md">
                            <img :src="getRealColValue(data, col.field)" class="w-full h-full object-cover" 
                                 onerror="this.src='data:image/svg+xml;charset=UTF-8,%3Csvg width=\'80\' height=\'80\' xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 80 80\' preserveAspectRatio=\'none\'%3E%3Cdefs%3E%3Cstyle type=\'text/css\'%3E%23holder_16e13f8404e text %7B fill%3A%23eceeef%3Bfont-weight%3Abold%3Bfont-family%3AArial%2C Helvetica%2C Open Sans%2C sans-serif%2C monospace%3Bfont-size%3A6pt %7D %3C/style%3E%3C/defs%3E%3Cg id=\'holder_16e13f8404e\'%3E%3Crect width=\'80\' height=\'80\' fill=\'%2355595c\'%3E%3C/rect%3E%3Cg%3E%3Ctext x=\'26\' y=\'43\'%3ENone%3C/text%3E%3C/g%3E%3C/g%3E%3C/svg%3E'" />
                        </div>
                        <div v-else class="w-16 h-16 flex items-center justify-center text-gray-400 border border-gray-200 rounded-md bg-gray-50">
                            No Image
                        </div>
                    </div>
                </span>
                <span v-else-if="col.col_type == 'text'">
                    {{
                        col.col_display == "badge_array"
                            ? getFormatBadgeArray(
                                getRealColValue(data, col.field),
                                col.col_badge_array_attr
                            )
                            : getRealColValue(data, col.field)
                    }}
                </span>
                <span v-else-if="col.col_type == 'html'" v-html="col.col_body ? col.col_body(data) : getRealColValue(data, col.field)"></span>
                <span v-else-if="col.col_type == 'status'">
                    {{
                        getStatus(data,col.field)
                    }}
                </span>
                <i
                    class="pi"
                    :class="{
                        'pi-times-circle text-red-500':
                            getRealColValue(data, col.field) == 0,
                        'pi-check-square text-green-500':
                            getRealColValue(data, col.field) == 1,
                    }"
                    v-else-if="col.col_type == 'boolean'"
                />
                <span v-else-if="col.col_type == 'date'">
                    {{ formatDate(getRealColValue(data, col.field), col.col_format) }}
                </span>
                <span v-else-if="col.col_type == 'numeric'">
                    {{
                        col.col_display == "currency"
                            ? formatCurrency(getRealColValue(data, col.field))
                            : col.col_display == "kilo"
                            ? formatNumeric(getRealColValue(data, col.field))
                            : getRealColValue(data, col.field)
                    }}
                </span>
            </template>
        </Column>
    </DataTable>
</template>

<script>
import { defineComponent } from "vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Input from "@/Components/Input";
import ButtonLinkShow from "@/Components/ButtonLinkShow";
import TriStateCheckbox from "primevue/tristatecheckbox";
import Calendar from "primevue/calendar";
import { FilterMatchMode, FilterOperator } from "primevue/api";
import { Link } from "@inertiajs/inertia-vue3";
import moment from "moment";

const TOTAL_ROW_SHOWED = 10;

export default defineComponent({
    props: ["config"],
    components: {
        DataTable,
        Column,
        Button,
        Input,
        TriStateCheckbox,
        Calendar,
        Link,
        ButtonLinkShow,
    },
    data() {
        return {
            totalRowShowed: TOTAL_ROW_SHOWED,
            total_records: 0,
            dataSourceList: null,
            selectedDataSource: [],
            selectAll: false,
            filters: null,
            lazyParams: {},
        };
    },
    created() {
        this.initFilter();
    },
    mounted() {
        let sortOrder = "ASC"; // DEFAULT ORDER
        let sortBy = "created_at"; // DEFAULT SORT

        if (this.config.sortBy !== undefined) {
            sortBy = this.config.sortBy;
        }

        if (this.config.sortOrder !== undefined) {
            sortOrder = this.config.sortOrder;
        } else {
            if (sortBy == "created_at") {
                sortOrder = "DESC";
            }
        }
        this.lazyParams = {
            rows: this.totalRowShowed,
            filters: this.filters,
            sortField: sortBy,
            sortOrder: sortOrder == "ASC" ? 1 : -1,
        };
        this.loadData();
    },
    watch: {
        selectedDataSource: {
            handler(newValue, oldValue) {
                this.$emit("getSelectedData", this.selectedDataSource);
            },
        },
    },
    methods: {
        editData: function() {
        },
        exportCSV: function (event) {
            this.$refs.dt.exportCSV();
        },

        exportExcel() {
            import('xlsx').then((xlsx) => {
                const worksheet = xlsx.utils.json_to_sheet(this.dataSourceList);
                const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
                const excelBuffer = xlsx.write(workbook, {
                    bookType: 'xlsx',
                    type: 'array'
                });

                this.saveAsExcelFile(excelBuffer, 'data_tumbas');
            }).catch((error) => {
                console.error('Error importing xlsx:', error);
            });
        },
        saveAsExcelFile(buffer, fileName) {
            import('file-saver').then((module) => {
                if (module && module.default) {
                    const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
                    const EXCEL_EXTENSION = '.xlsx';
                    const data = new Blob([buffer], { type: EXCEL_TYPE });

                    module.default.saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
                }
            }).catch((error) => {
                console.error('Error importing file-saver:', error);
            });
        },

        deleteData: function(event, data) {
            let self = this;
            self.$confirm.require({
                target: event.currentTarget,
                message:
                    "Delete selected record ?",
                icon: "pi pi-info-circle",
                acceptClass: "p-button-danger",
                accept: () => {
                    axios
                        .delete(route(self.config.dtSourceApiRoute.destroy, {
                            id: data.id,
                        }))
                        .then(function (response) {
                            self.loadData();
                        })
                        .catch(function (error) {
                            //
                        })
                        .then(function () {
                            //
                        });
                },
                reject: () => {
                    //
                },
            });
        },
        getFormatBadgeArray: function (value, selected_attr) {
            let format_badge_array = _.map(value, selected_attr);
            return _.join(format_badge_array, ", ");
        },
        getStatus: function (data, selected_attr) {
            // console.log(data[selected_attr]);
            switch(data[selected_attr]) {
                case '1':
                  return  'TERDAFTAR';
                    break;
                case '2':
                    return 'KLARIFIKASI';
                    break;
                case '3':
                    return 'TERVERIFIKASI';
                    break;
                case '4':
                    return'TERTUNDA';
                    break;
                default:
                    return data[selected_attr];
            };
        },
        getRealColValue: function (data, field) {
            let array_field = field.split(".");
            array_field.forEach(function (item, index) {
                array_field[index] = _.snakeCase(item); // => Untuk konversi jika ada value yang camelCase, contoh untuk jika ada relasi
            });
            let colValue = data[field] ?? null;
            if (array_field.length == 2) {
                /* --- JIKA FIELD NYA BERELASI BERTINGAKT SEPERTI : pegawai.nama_lengkap -- */
                colValue = null;
                if (!_.isNull(data[array_field[0]])) {
                    colValue = !_.isNull(data[array_field[0]][array_field[1]])
                        ? data[array_field[0]][array_field[1]]
                        : null;
                }
            }
            if (array_field.length == 3) {
                /* --- JIKA FIELD NYA BERELASI BERTINGAKT SEPERTI : pegawai.kelas.nama_kelas -- */
                colValue = null;
                if (!_.isNull(data[array_field[0]])) {
                    if (!_.isNull(data[array_field[0]][array_field[1]])) {
                        colValue = !_.isNull(
                            data[array_field[0]][array_field[1]][array_field[2]]
                        )
                            ? data[array_field[0]][array_field[1]][array_field[2]]
                            : null;
                    }
                }
            }
            if (array_field.length == 4) {
                /* --- JIKA FIELD NYA BERELASI BERTINGAKT SEPERTI : unit.pegawai.kelas.nama_kelas -- */
                colValue = null;
                if (!_.isNull(data[array_field[0]])) {
                    if (!_.isNull(data[array_field[0]][array_field[1]])) {
                        if (!_.isNull(data[array_field[0]][array_field[1]][array_field[2]])) {
                            colValue = !_.isNull(
                                data[array_field[0]][array_field[1]][array_field[2]][array_field[3]]
                            )
                                ? data[array_field[0]][array_field[1]][array_field[2]][array_field[3]]
                                : null;
                        }
                    }
                }
            }
            if (array_field.length == 5) {
                /* --- JIKA FIELD NYA BERELASI BERTINGAKT SEPERTI : departemen.unit.pegawai.kelas.nama_kelas -- */
                colValue = null;
                if (!_.isNull(data[array_field[0]])) {
                    if (!_.isNull(data[array_field[0]][array_field[1]])) {
                        if (!_.isNull(data[array_field[0]][array_field[1]][array_field[2]])) {
                            if (!_.isNull(data[array_field[0]][array_field[1]][array_field[2]][array_field[3]])) {
                                colValue = !_.isNull(
                                data[array_field[0]][array_field[1]][array_field[2]][array_field[3]][array_field[4]]
                            )
                                ? data[array_field[0]][array_field[1]][array_field[2]][array_field[3]][array_field[4]]
                                : null;
                            }
                        }
                    }
                }
            }
            return colValue;
        },
        formatNumeric: function (value) {
            return _.isNull(value)
                ? null
                : new Number(value).toLocaleString("id-ID");
        },
        formatCurrency: function (value) {
            return _.isNull(value)
                ? null
                : new Number(value).toLocaleString("id-ID", {
                    style: "currency",
                    currency: "IDR",
                });
        },
        resetSelection: function () {
            this.selectAll = false;
            this.selectedDataSource = [];
        },
        resetFilter: function () {
            this.resetSelection();
            this.initFilter();
            this.onFilter();
        },
        initFilter: function () {
            let self = this;
            self.filters = {
                global: {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS,
                },
            };
            self.config.defineCols.forEach(function (col, index) {
                if (col.col_type == "text") {
                    self.filters[col.field] = {
                        operator: FilterOperator.AND,
                        constraints: [
                            {
                                value: null,
                                matchMode: FilterMatchMode.CONTAINS,
                            },
                        ],
                    };
                } else if (col.col_type == "boolean") {
                    self.filters[col.field] = {
                        value: null,
                        matchMode: FilterMatchMode.EQUALS,
                    };
                } else if (col.col_type == "date") {
                    self.filters[col.field] = {
                        operator: FilterOperator.AND,
                        constraints: [
                            { value: null, matchMode: FilterMatchMode.DATE_IS },
                        ],
                    };
                } else if (col.col_type == "numeric") {
                    self.filters[col.field] = {
                        operator: FilterOperator.AND,
                        constraints: [
                            { value: null, matchMode: FilterMatchMode.EQUALS },
                        ],
                    };
                }
            });
        },
        addDataSourceList: function (data) {
            //
        },
        changeDataSourceList: function (data) {
            /* -------------- Untuk merubah nilai di tabel setelah di edit -------------- */
            let self = this;
            self.config.defineCols.forEach(function (col) {
                // if (col.col_type == "text") {
                //     self.selectedDataSource[0][col.field] = data[col.field];
                // } else if (col.col_type == "boolean") {
                //     self.selectedDataSource[0][col.field] = data[col.field]
                //         ? 1
                //         : 0;
                // }

                if (col.col_type == "boolean") {
                    self.selectedDataSource[0][col.field] = data[col.field]
                        ? 1
                        : 0;
                } else {
                    self.selectedDataSource[0][col.field] = data[col.field];
                }
            });
        },
        loadData: function () {
            let self = this;
            axios
                .get(route(this.config.dtSourceApiRoute.list), {
                    params: {
                        dt_params: JSON.stringify(self.lazyParams),
                        searchable_columns: JSON.stringify(
                            this.config.globalFilter
                        ),
                    },
                })
                .then((res) => {
                    self.dataSourceList = res.data.payload.data;
                    self.total_records = res.data.payload.total;
                })
                .catch(function (error) {
                    //
                })
                .then(function () {
                    //
                });
        },
        onPaginate: function (event) {
            this.lazyParams = event;
            this.loadData();
        },
        onSort: function (event) {
            this.lazyParams = event;
            this.loadData();
        },
        onFilter: function () {
            this.lazyParams.filters = this.filters;
            this.lazyParams.page = 0;
            this.loadData();
        },
        onSelectAllChange: function (event) {
            let _selectAll = event.checked;
            if (_selectAll) {
                this.selectAll = true;
                this.selectedDataSource = this.dataSourceList;
            } else {
                this.selectAll = false;
                this.selectedDataSource = [];
            }
        },
        onRowSelect: function () {
            this.selectAll =
                this.selectedDataSource.length === this.total_records;
        },
        onRowUnselect: function () {
            this.selectAll = false;
        },
        dropData: function (ids) {
            let self = this;
            axios
                .post(route(self.config.dtSourceApiRoute.drop), {
                    ids: ids,
                })
                .then(function (response) {
                    // self.selectedDataSource.forEach(function(item, index){
                    self.loadData();
                    // });
                    self.selectedDataSource = [];
                })
                .catch(function (error) {
                    //
                })
                .then(function () {
                    //
                });
        },
    },
});
</script>
